import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  input
} from "@angular/core";
import { InvoiceAddRequest } from "../../interfaces/invoice_add_request";
import {
  IonicModule,
  LoadingController,
  ModalController,
} from "@ionic/angular";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ModeService } from "src/app/utils/mode.service";
import { Store } from "@ngrx/store";
import * as customerSelector from "../../../auth/ngrx/customer.selectors";
import * as invoiceSelector from "../../invoice-ngrx/invoices.selectors";
import * as paymentSelector from "../../payment-ngrx/payments.selectors";
import {
  addInvoiceStatus,
  createAndSendInvoiceMail,
  sendMail,
} from "../../invoice-ngrx/invoices.actions";
import { createAndSendPaymentMail } from "../../payment-ngrx/payments.actions";
import { PaymentAddRequest } from "../../interfaces/payment_add_request";
import { SendRequest } from "../../interfaces/send_request";
import { Subject, takeUntil } from "rxjs";
import { updateCustomer } from "src/app/auth/ngrx/customer.actions";
import { InvoiceDto } from "../../interfaces/invoice_dto";
import { StudentDto } from "src/app/students/interfaces/student_dto";
import { Plans } from "src/app/shared/models/plans.enum";

@Component({
    selector: "app-mail-dialog",
    templateUrl: "./mail-dialog.component.html",
    styleUrls: ["./mail-dialog.component.scss"],
    imports: [IonicModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MailDialogComponent implements OnInit, OnDestroy {
  readonly invoiceAddRequest = input<InvoiceAddRequest>(undefined);
  readonly paymentAddRequest = input<PaymentAddRequest>(undefined);
  readonly invoiceReminderData = input<{
    invoice: InvoiceDto;
    student: StudentDto;
}>(undefined);
  readonly pdf = input<string>(undefined);
  readonly file = input<File>(undefined);

  requestData;
  mailForm: FormGroup;
  customerSignal = this.store.selectSignal(customerSelector.selectCustomer);
  isGeneratingInvoice$ = this.store.select(invoiceSelector.selectIsLoading);
  isGeneratingPayment$ = this.store.select(paymentSelector.selectIsLoading);
  customerEntitlementsSignal = this.store.selectSignal(customerSelector.selectEntitlements);
  destroy$: Subject<void> = new Subject();
  messageTemplate: string;
  Plans = Plans;

  constructor(
    public modeService: ModeService,
    private store: Store<any>,
    private modalController: ModalController,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private loadingCtrl: LoadingController
  ) {
    this.mailForm = this.fb.group({
      mail: ["", Validators.required],
      subject: [""],
      message: [""],
      sendCopy: [false],
      saveAsTemplate: [false],
      enableOnlinePayment: [(this.customerSignal().online_payment && (this.customerEntitlementsSignal()?.includes(Plans.premium) || this.customerEntitlementsSignal()?.includes(Plans.premium_promo))) ?? false],
    });
  }

  ngOnInit() {
    this.requestData = this.invoiceAddRequest() ?? this.paymentAddRequest();
    const invoiceAddRequest = this.invoiceAddRequest();
    const paymentAddRequest = this.paymentAddRequest();
    const invoiceReminderData = this.invoiceReminderData();
    this.messageTemplate = invoiceAddRequest
      ? `Hallo ${invoiceAddRequest?.student?.forename},

Vielen Dank für dein Vertrauen in mich. Ich habe dir die Rechnung für die erbrachte Leistung angehängt.

Ich freue mich auf die nächste Lektion mit dir.

Liebe Grüsse`
      : paymentAddRequest
      ? `Hallo ${paymentAddRequest?.student?.forename},

Vielen Dank für deine Zahlung. Ich freue mich auf die nächste Lektion mit dir.

Liebe Grüsse`
      : invoiceReminderData
      ? `Hallo ${invoiceReminderData?.student?.forename},

In der Hektik des Alltags geht manches schnell mal unter. Für die beigelegte Rechnung konnte Quick Drive leider noch keinen Zahlungseingang feststellen.

Wir wären dir deshalb dankbar, wenn du den ausstehenden Betrag in den nächsten Tagen begleichen könntest.

Falls die Rechnung in der Zwischenzeit bezahlt wurde, betrachte dieses Schreiben bitte als gegenstandslos.

Freundliche Grüsse`
      : null;

    const invoiceReminderDataValue = this.invoiceReminderData();
    if (this.invoiceAddRequest() && this.customerSignal().invoice_mail_template) {
      // replace {Vorname} with the forename of the student and {Nachname} with the surname of the student
      this.messageTemplate = this.customerSignal()
        .invoice_mail_template.replace(
          "{Vorname}",
          this.requestData.student.forename
        )
        .replace("{Nachname}", this.requestData.student.surname);
    } else if (
      this.paymentAddRequest() &&
      this.customerSignal().payment_mail_template
    ) {
      // replace {Vorname} with the forename of the student and {Nachname} with the surname of the student
      this.messageTemplate = this.customerSignal()
        .payment_mail_template.replace(
          "{Vorname}",
          this.requestData.student.forename
        )
        .replace("{Nachname}", this.requestData.student.surname);
    } else if (
      invoiceReminderDataValue &&
      this.customerSignal().reminder_mail_template
    ) {
      this.messageTemplate = this.customerSignal()
        .reminder_mail_template.replace(
          "{Vorname}",
          invoiceReminderDataValue?.student.forename
        )
        .replace("{Nachname}", invoiceReminderDataValue?.student.surname);
    }
    this.patchMailForm();
    this.cdr.detectChanges();

    this.patchMailForm();
  }

  patchMailForm() {
    const invoiceReminderData = this.invoiceReminderData();
    const invoiceReminderDataValue = this.invoiceReminderData();
    this.mailForm.patchValue({
      mail:
        this.requestData?.student?.mail_invoice ||
        this.requestData?.student?.mail_student ||
        invoiceReminderData?.student.mail_invoice ||
        invoiceReminderData?.student.mail_student,
      subject: invoiceReminderDataValue
        ? `Zahlungserinnerung - CHF ${
            invoiceReminderDataValue?.invoice?.amount -
            invoiceReminderDataValue?.invoice?.paid_amount
          } vom ${new Date(
            invoiceReminderDataValue?.invoice?.invoice_date
          ).toLocaleDateString("fr-CH")}`
        : `${this.invoiceAddRequest() ? "Rechnung" : "Zahlungsbestätigung"} - ${
            this.requestData.student.forename
          } ${this.requestData.student.surname} - CHF ${
            this.requestData.amount
          }`,
      message: this.messageTemplate,
      sendCopy: false,
      saveAsTemplate: false,
    });
  }

  async cancel() {
    if (await this.modalController.getTop()) {
      this.modalController.dismiss();
    }
  }

  async sendMail() {
    const loading = await this.loadingCtrl.create({
      message: "Senden...",
    });

    loading.present();
    let mailForm = this.mailForm.value;

    const invoiceAddRequest = this.invoiceAddRequest();
    let sendRequest = {
      student: this.requestData?.student || this.invoiceReminderData()?.student,
      customer: this.customerSignal(),
      sender: this.customerSignal().mail_customer,
      recipient: mailForm.mail as string,
      bcc: mailForm.sendCopy ? this.customerSignal().mail_customer : "",
      subject: mailForm.subject as string,
      content: mailForm.message as string,
      payment_link: this.invoiceReminderData()?.invoice?.payment_link || null,
      date: new Date(
        this.invoiceAddRequest()?.invoice_date ??
          this.paymentAddRequest()?.date ??
          this.invoiceReminderData()?.invoice?.invoice_date
      ).toLocaleDateString("fr-CH"),
      due_date: invoiceAddRequest?.due_date
        ? new Date(invoiceAddRequest?.due_date).toLocaleDateString("fr-CH")
        : null,
      total:
        Math.round(
          (this.invoiceAddRequest()?.total_amount ??
            this.paymentAddRequest()?.amount ??
            this.invoiceReminderData()?.invoice?.amount -
              this.invoiceReminderData()?.invoice?.paid_amount) * 1e2
        ) / 1e2,
      attachment: this.pdf(),
      invoiceId: null,
    } as SendRequest;

    const invoiceAddRequestValue = this.invoiceAddRequest();
    const paymentAddRequest = this.paymentAddRequest();
    const invoiceReminderData = this.invoiceReminderData();
    if (invoiceAddRequestValue) {
      this.store.dispatch(
        createAndSendInvoiceMail({
          sendRequest: sendRequest,
          invoiceAddRequest: invoiceAddRequestValue,
          file: this.file(),
          payOnline: mailForm.enableOnlinePayment && invoiceAddRequestValue?.total_amount > 0,
        })
      );

      if (mailForm.saveAsTemplate) {
        let mailTemplate = mailForm.message
          .replace(this.requestData.student.forename, "{Vorname}")
          .replace(this.requestData.student.surname, "{Nachname}");
        let updatedCustomer = {
          ...this.customerSignal(),
          invoice_mail_template: mailTemplate,
        };
        this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
      }

      this.isGeneratingInvoice$
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (isLoading) => {
          if (!isLoading) {
            loading.dismiss();
            if ((await this.modalController.getTop()) !== undefined) {
              this.modalController.dismiss(true);
            }
          }
        });
    } else if (paymentAddRequest) {
      this.store.dispatch(
        createAndSendPaymentMail({
          sendRequest: sendRequest,
          paymentAddRequest: paymentAddRequest,
          file: this.file(),
        })
      );

      if (mailForm.saveAsTemplate) {
        let mailTemplate = mailForm.message
          .replace(this.requestData.student.forename, "{Vorname}")
          .replace(this.requestData.student.surname, "{Nachname}");
        let updatedCustomer = {
          ...this.customerSignal(),
          payment_mail_template: mailTemplate,
        };
        this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
      }

      this.isGeneratingPayment$
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (isLoading) => {
          if (!isLoading) {
            loading.dismiss();
            if ((await this.modalController.getTop()) !== undefined) {
              this.modalController.dismiss(true);
            }
          }
        });
    } else if (invoiceReminderData) {
      if (mailForm.saveAsTemplate) {
        let mailTemplate = mailForm.message
          .replace(invoiceReminderData?.student.forename, "{Vorname}")
          .replace(invoiceReminderData?.student.surname, "{Nachname}");
        let updatedCustomer = {
          ...this.customerSignal(),
          reminder_mail_template: mailTemplate,
        };
        this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
      }

      this.store.dispatch(
        addInvoiceStatus({
          invoiceId: invoiceReminderData?.invoice?.invoice_id,
          status: "reminded",
        })
      );
      this.store.dispatch(
        sendMail({
          sendRequest: sendRequest,
          payOnline: false,
        })
      );
      loading.dismiss();
      if ((await this.modalController.getTop()) !== undefined) {
        this.modalController.dismiss(true);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
